import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  Button,
  LinearProgress,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { layoutCSS } from "../component/common/commonStyle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetDownloadRequestListQuery } from "../store/services/reportApi";
import { useGetCustomerServiceQuery } from "../store/services/resellerApi";
import {
  useGetdownloadcentercategoryQuery,
  useDownloadreportrequestMutation,
} from "../store/services/commonApi";
import {
  useLazyFlowOrderReportQuery,
  useLazySmsflowreportcountsQuery,
} from "../store/services/flowApi";
import { useLazyRcsflowreportcountsQuery } from "../store/services/rcstemplateApi";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { useDeleteDownloadRequestMutation } from "../store/services/clickerApi";
import {
  useGetDownloadcenterlistQuery,
  useGetPhoneNUmberQuery,
  useLazyGiveDownloadrequestforreportQuery,
  useLazyDownloadreportQuery,
  useDeletedownloadcenterfileMutation,
} from "../store/services/whatsappApi";

/* ────────────────────────── Styles ────────────────────────── */
// Shared look for every filter (mirrors the SMS report page's `inputSx`)
const filterFieldSx = {
  borderRadius: "0.5rem",
  minWidth: 100,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

// Date picker: 140px wide (matches SMS report page)
const datePickerSx = {
  width: 140,
  ...filterFieldSx,
};

// Select fields: 140px wide, glass + hover/focus glow
const selectSx = {
  width: 140,
  ...filterFieldSx,
  "& .MuiOutlinedInput-root": {
    ...filterFieldSx["& .MuiOutlinedInput-root"],
    borderRadius: "8px",
    height: 35,
    background: "rgba(255, 255, 255, 0.8)",
    transition: "all 0.2s ease",
    "&:hover": { boxShadow: "0 4px 12px rgba(14,165,233,0.1)" },
    "&.Mui-focused": { boxShadow: "0 0 0 2px rgba(14,165,233,0.2)" },
  },
  "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "6px 8px" },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", color: "#475569" },
};

// Table cells
const cellSX = {
  fontSize: "0.75rem",
  padding: "1px",
  textAlign: "center",
  borderBottom: "0.5px solid #1b1b1bff",
  borderRight: "0.5px solid #1b1b1bff",
};

/* ───────────────── WhatsApp report → { slug, id } ─────────────
   `id` is OUR OWN (1–4) sent to the WA endpoint, NOT the DB Report_type_id.
   Lookup order: DB Report_type_id (7–10) first, then normalized name. */
const WA_BY_DB_ID = {
  7: { slug: "wa_report", id: 1 },
  8: { slug: "wa_count", id: 2 },
  9: { slug: "wa_click", id: 3 },
  10: { slug: "wa_click_count", id: 4 },
};

// "WA Click Count Report" → "waclickcountreport"
const normalizeName = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");

const WA_BY_NAME = {
  wareport: WA_BY_DB_ID[7],
  wacount: WA_BY_DB_ID[8],
  wacountreport: WA_BY_DB_ID[8],
  waclick: WA_BY_DB_ID[9],
  waclickreport: WA_BY_DB_ID[9],
  waclickcount: WA_BY_DB_ID[10],
  waclickcountreport: WA_BY_DB_ID[10],
};

// Friendly label for rows coming back from the WA list endpoint
const WA_LABEL_BY_SLUG = {
  wa_report: "WA Report",
  wa_count: "WA Count Report",
  wa_click: "WA Click Report",
  wa_click_count: "WA Click Count Report",
};

// Takes the WHOLE selected report object (not just the name)
const getWhatsAppReport = (report) => {
  if (!report) return null;
  return (
    WA_BY_DB_ID[Number(report.Report_type_id)] ||
    WA_BY_NAME[normalizeName(report.Report_type_name)] ||
    null
  );
};

/* ───────────────── Flow export helpers ─────────────────────── */
const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm:ss") : value;
};

const mapSmsRow = (item, flow) => ({
  "Campaign Flow ID": flow.campaignflow_id,
  "Flow Name": flow.campaign_name || "-",
  "Created At": formatDateTime(item.submit_date),
  "Phone Number": item.ph_no || "-",
  "Sender ID": item.sender_id || "-",
  Status: item.stat || "-",
  "DND Status": item.dnd_status || "-",
  "Status Updated Time": formatDateTime(item.done_date),
});

const mapRcsRow = (item, flow) => ({
  "Campaign Flow ID": flow.campaignflow_id,
  "Flow Name": flow.campaign_name || "-",
  "Send Date": formatDateTime(item.send_time || item.submit_date || item.created_at),
  "Done Time": formatDateTime(item.done_time || item.updated_at),
  Mobile: item.user || item.ph_no || "-",
  Status: item.message_status || item.stat || "-",
  Provider: item.provider || "-",
  "Error Code": item.errcode || item.error_code || "-",
  "Error Description": item.reason || item.errordescription || item.error_description || "-",
});

const detectFlowServices = (rows) => {
  const services = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key.startsWith("order") && !key.includes("_")) {
        const val = String(row[key] || "").trim().toLowerCase();
        if (val === "sms" || val === "rcs") {
          services.add(val);
        }
      }
    });
  });
  return services;
};

const SERVICE_CHIP_COLOR = {
  sms: { bgcolor: "#e3f2fd", color: "#1565c0" },
  rcs: { bgcolor: "#fff3e0", color: "#e65100" },
};

/* Normalize a WA download-center-list row to the generic shape. */
const normalizeDownloadCenterRow = (row) => {
  const rawName = row.Report_type_name || row.report_type_name || row.report_type || "-";
  return {
    dlr_request_id: row.dlr_request_id || row.id,
    Report_type_name: WA_LABEL_BY_SLUG[rawName] || rawName,
    fromdate: row.fromdate,
    todate: row.todate,
    createdate: row.createdate,
    status: row.status,
    complitiondate: row.complitiondate,
    filepath: row.filepath || row.download_url,
    description: row.description,
  };
};

const formatStatusLabel = (status) => {
  if (!status) return "-";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

/* ─────────────────────── Component ────────────────────────── */
const DownloadCenter = () => {
  const today = dayjs();
  const defaultFrom = today;

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(today);
  const [reportType, setReportType] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [dateError, setDateError] = useState("");

  // Flow report state
  const [isFlowSearching, setIsFlowSearching] = useState(false);
  const [flowRows, setFlowRows] = useState([]);
  const [flowSearchDetails, setFlowSearchDetails] = useState(null);
  const [downloadingFlowId, setDownloadingFlowId] = useState(null);
  const [flowRowProgress, setFlowRowProgress] = useState(null);

  // Customer ID from JWT
  const [customerId, setCustomerId] = useState(null);
  const [lastSearchDetails, setLastSearchDetails] = useState(null);

  // Tracks whether the current search targeted the WhatsApp service.
  const [isWhatsAppSearch, setIsWhatsAppSearch] = useState(false);
  const [whatsappPhoneNumber, setWhatsappPhoneNumber] = useState("");
  const [whatsappWabaId, setWhatsappWabaId] = useState("");

  // Queries & mutations
  const { data: customerServiceData, isLoading: isServiceLoading } =
    useGetCustomerServiceQuery();
  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetdownloadcentercategoryQuery();

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 25,
    startdate: defaultFrom.format("YYYY-MM-DD"),
    enddate: today.format("YYYY-MM-DD"),
    _ts: Date.now(),
  });

  const [deleteDownloadRequest, { isLoading: isDeleting }] =
    useDeleteDownloadRequestMutation();
  const [deleteWhatsappDownloadRequest, { isLoading: isDeletingWhatsapp }] =
    useDeletedownloadcenterfileMutation();

  const { data, isLoading, isFetching, refetch } = useGetDownloadRequestListQuery(
    queryParams,
    { skip: isWhatsAppSearch },
  );

  const [requestDownload, { isLoading: isRequesting }] =
    useDownloadreportrequestMutation();

  // WA list — no polling. Fetches only on search / explicit refresh.
  const {
    data: downloadCenterListData,
    isLoading: isDownloadCenterLoading,
    isFetching: isDownloadCenterFetching,
    refetch: refetchDownloadCenter,
  } = useGetDownloadcenterlistQuery(
    {
      customer_id: customerId,
      start_date: queryParams.startdate,
      end_date: queryParams.enddate,
      _ts: queryParams._ts,
    },
    { skip: !customerId || !isWhatsAppSearch },
  );

  // Lazy WA request trigger — sends the report slug on button click.
  const [triggerWARequest, { isLoading: isRequestingWA }] =
    useLazyGiveDownloadrequestforreportQuery();

  // Lazy WA download — fires only when the user clicks a row's download button.
  const [triggerWADownload] = useLazyDownloadreportQuery();
  const [downloadingWAId, setDownloadingWAId] = useState(null);

  const [triggerFlowOrderReport] = useLazyFlowOrderReportQuery();
  const [triggerSmsCounts] = useLazySmsflowreportcountsQuery();
  const [triggerRcsCounts] = useLazyRcsflowreportcountsQuery();

  /* ── Extract customer ID from token ── */
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Access token not found");
        return;
      }
      const decoded = jwtDecode(token);
      const id =
        decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomerId(id);
    } catch (err) {
      toast.error("Invalid access token format");
    }
  }, []);

  const serviceOptions = useMemo(() => {
    if (!customerServiceData?.data) return [];
    const seen = new Set();
    return customerServiceData.data.reduce((acc, item) => {
      if (item.service && !seen.has(item.service.service_id)) {
        seen.add(item.service.service_id);
        acc.push({
          service_id: item.service.service_id,
          service_name: item.service.service_name,
        });
      }
      return acc;
    }, []);
  }, [customerServiceData]);

  /* ── Identify the WhatsApp service id (by name, case-insensitive) ── */
  const whatsappServiceId = useMemo(() => {
    const wa = serviceOptions.find((s) =>
      /whatsapp|^wa\b/i.test((s.service_name || "").trim()),
    );
    return wa ? Number(wa.service_id) : null;
  }, [serviceOptions]);

  // True only when the user has explicitly selected the WhatsApp service.
  const isWhatsAppService = useMemo(
    () =>
      whatsappServiceId != null &&
      serviceType !== "" &&
      Number(serviceType) === whatsappServiceId,
    [serviceType, whatsappServiceId],
  );

  const { data: whatsappPhoneData, isLoading: isWhatsappPhoneLoading } =
    useGetPhoneNUmberQuery(customerId, {
      skip: !customerId || !isWhatsAppService,
    });
  const whatsappPhoneNumbers = useMemo(
    () => whatsappPhoneData?.phone_numbers || [],
    [whatsappPhoneData],
  );
  const whatsappWabaIds = useMemo(
    () => [...new Set(whatsappPhoneNumbers.map((phone) => phone.waba_id).filter(Boolean))],
    [whatsappPhoneNumbers],
  );

  // Only show phone numbers that belong to the selected WABA
  const filteredPhoneNumbers = useMemo(
    () =>
      whatsappWabaId
        ? whatsappPhoneNumbers.filter((p) => p.waba_id === whatsappWabaId)
        : whatsappPhoneNumbers,
    [whatsappPhoneNumbers, whatsappWabaId],
  );

  // Report types for the selected service.
  // Matches on Service_id, with a service_name fallback in case the two APIs use different ids.
  const reportTypeOptions = useMemo(() => {
    const allCategories = categoriesData?.data || [];
    if (!serviceType) return allCategories;
    const serviceId = Number(serviceType);
    const selectedServiceName = normalizeName(
      serviceOptions.find((s) => Number(s.service_id) === serviceId)?.service_name,
    );
    return allCategories.filter(
      (cat) =>
        Number(cat.Service_id) === serviceId ||
        (selectedServiceName && normalizeName(cat.service_name) === selectedServiceName),
    );
  }, [categoriesData, serviceType, serviceOptions]);

  useEffect(() => {
    setReportType("");
    if (!isWhatsAppService) {
      setWhatsappPhoneNumber("");
      setWhatsappWabaId("");
    }
  }, [serviceType, isWhatsAppService]);

  const selectedReport = useMemo(() => {
    return reportTypeOptions.find(
      (cat) => Number(cat.Report_type_id) === Number(reportType),
    );
  }, [reportTypeOptions, reportType]);

  /* ── Date handlers ── */
  const handleFromDateChange = (val) => {
    setFromDate(val);
    setDateError("");
    if (val && toDate) {
      const maxTo = val.add(1, "month");
      if (toDate.isAfter(maxTo)) setToDate(maxTo);
    }
  };

  const handleToDateChange = (val) => {
    if (!fromDate) {
      setDateError("Please select From Date first");
      return;
    }
    if (val && val.isBefore(fromDate, "day")) {
      setDateError("To Date cannot be before From Date");
      return;
    }
    const maxTo = fromDate.add(1, "month");
    if (val && val.isAfter(maxTo, "day")) {
      setDateError(
        `To Date cannot exceed 1 month from From Date (max: ${maxTo.format("DD/MM/YYYY")})`,
      );
      return;
    }
    setDateError("");
    setToDate(val);
  };

  const handleDeleteRequest = async (dlr_request_id) => {
    if (dlr_request_id == null) {
      toast.error("Request ID not found");
      return;
    }

    if (isWhatsAppSearch && !customerId) {
      toast.error("Customer ID not available");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this download request?",
    );
    if (!confirmDelete) return;

    try {
      if (isWhatsAppSearch) {
        await deleteWhatsappDownloadRequest({
          customer_id: customerId,
          dlr_request_id,
        }).unwrap();
      } else {
        await deleteDownloadRequest(dlr_request_id).unwrap();
      }
      toast.success("Download request deleted successfully");
      if (isWhatsAppSearch) refetchDownloadCenter();
      else refetch();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(err?.data?.message || "Failed to delete download request");
    }
  };

  /* ── Search — routing decided by SERVICE, not by report name ── */
  const handleSearch = () => {
    if (!fromDate || !toDate) {
      setDateError("Please select both From Date and To Date");
      return;
    }

    setFlowSearchDetails(null);
    setFlowRows([]);

    // WhatsApp is the only service that uses the WA list endpoint.
    setIsWhatsAppSearch(isWhatsAppService);

    setLastSearchDetails({
      startDate: fromDate,
      endDate: toDate,
      reportTypeName: selectedReport?.Report_type_name,
      searchAt: dayjs(),
    });

    setQueryParams({
      page: 1,
      limit: 25,
      startdate: fromDate.startOf("day").format("YYYY-MM-DD"),
      enddate: toDate.endOf("day").format("YYYY-MM-DD"),
      _ts: Date.now(),
      ...(reportType && { report_type_id: reportType }),
    });
  };

  /* ── Flow Report search ── */
  const handleFlowSearch = async () => {
    if (!fromDate || !toDate) {
      setDateError("Please select both From Date and To Date");
      return;
    }

    setIsFlowSearching(true);
    setFlowRows([]);
    setFlowSearchDetails(null);

    try {
      const res = await triggerFlowOrderReport({
        startDate: fromDate.format("YYYY-MM-DD"),
        endDate: toDate.format("YYYY-MM-DD"),
        page: 1,
        limit: 1000,
      }).unwrap();
      const rows = res?.data || [];
      setFlowRows(rows);
      setFlowSearchDetails({ startDate: fromDate, endDate: toDate, searchAt: dayjs() });
      if (!rows.length) {
        toast.info("No flows found for the selected date range");
      }
    } catch (err) {
      console.error("Flow search failed:", err);
      toast.error(err?.data?.message || "Failed to fetch flow report");
    } finally {
      setIsFlowSearching(false);
    }
  };

  /* ── Per-row Download (flow) ── */
  const handleDownloadFlowRow = async (flow) => {
    if (!customerId) {
      toast.error("Customer ID not available");
      return;
    }

    const servicesPresent = detectFlowServices([flow]);
    if (servicesPresent.size === 0) {
      toast.error("No SMS/RCS data found for this flow");
      return;
    }

    setDownloadingFlowId(flow.campaignflow_id);
    setFlowRowProgress({ completed: 0, total: servicesPresent.size });

    const smsRows = [];
    const rcsRows = [];
    let completed = 0;
    let failedCount = 0;

    for (const service of servicesPresent) {
      try {
        if (service === "sms") {
          const res = await triggerSmsCounts({
            campaignflow_id: flow.campaignflow_id,
            status: "sent",
          }).unwrap();
          (res?.data || []).forEach((item) => smsRows.push(mapSmsRow(item, flow)));
        } else if (service === "rcs") {
          const res = await triggerRcsCounts({
            campaignflow_id: flow.campaignflow_id,
            status: "total",
          }).unwrap();
          (res?.data || []).forEach((item) => rcsRows.push(mapRcsRow(item, flow)));
        }
      } catch (err) {
        failedCount += 1;
        console.error(`Flow export failed for ${service} / flow ${flow.campaignflow_id}:`, err);
      } finally {
        completed += 1;
        setFlowRowProgress({ completed, total: servicesPresent.size });
      }
    }

    const workbook = XLSX.utils.book_new();
    if (smsRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(smsRows), "SMS");
    }
    if (rcsRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rcsRows), "RCS");
    }

    if (workbook.SheetNames.length === 0) {
      toast.error("No records returned for this flow");
    } else {
      const safeName = String(flow.campaign_name || flow.campaignflow_id).replace(/[^a-z0-9]+/gi, "_");
      const fileName = `Flow_${safeName}_${flow.campaignflow_id}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      if (failedCount > 0) {
        toast.warning(`Export complete, but ${failedCount} of ${servicesPresent.size} calls failed`);
      } else {
        toast.success("Flow report downloaded successfully");
      }
    }

    setDownloadingFlowId(null);
    setFlowRowProgress(null);
  };

  /* ── Submit: WA path OR generic SMS/RCS path ── */
  const handleSubmit = async () => {
    if (!fromDate || !toDate || !reportType) {
      setDateError("Please select From Date, To Date, and a Report Type");
      return;
    }
    if (!customerId) {
      toast.error("Customer ID not available");
      return;
    }

    // --- WhatsApp path (decided by service, not by report name) ---
    if (isWhatsAppService) {
      const entry = getWhatsAppReport(selectedReport); // pass the whole object
      if (!entry) {
        toast.error(
          `Unsupported WhatsApp report: ${selectedReport?.Report_type_name || "unknown"}`,
        );
        return;
      }

      const payload = {
        customer_id: customerId,
        report_type: entry.slug, // "wa_report" | "wa_count" | "wa_click" | "wa_click_count"
        report_type_id: entry.id, // 1 | 2 | 3 | 4  ← OUR ids, never the DB id
        start_date: fromDate.startOf("day").format("YYYY-MM-DD"),
        end_date: toDate.endOf("day").format("YYYY-MM-DD"),
        ...(whatsappPhoneNumber && { phone_number: whatsappPhoneNumber }),
        ...(whatsappWabaId && { waba_id: whatsappWabaId }),
      };

      try {
        const res = await triggerWARequest(payload).unwrap();
        toast.success(
          `WhatsApp report requested (ID: ${res?.dlr_request_id ?? res?.id ?? "?"})`,
        );
        setQueryParams((current) => ({
          ...current,
          startdate: fromDate.startOf("day").format("YYYY-MM-DD"),
          enddate: toDate.endOf("day").format("YYYY-MM-DD"),
          _ts: Date.now(),
        }));
        setIsWhatsAppSearch(true);
      } catch (err) {
        console.error("WA request failed", err);
        toast.error(err?.data?.message || "Failed to submit WhatsApp download request");
      }
      return;
    }

    // --- Generic SMS/RCS path ---
    const payload = {
      Report_type_id: Number(reportType),
      customer_id: customerId,
      fromdate: fromDate.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      todate: toDate.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    };

    try {
      await requestDownload(payload).unwrap();
      toast.success("Download request submitted successfully!");
      refetch();
    } catch (err) {
      console.error("Request failed", err);
      toast.error(err?.data?.message || "Failed to submit download request");
    }
  };

  /* ── Manual refresh for the current table ── */
  const handleRefresh = () => {
    if (isWhatsAppSearch) refetchDownloadCenter();
    else refetch();
  };

  /* ── Reset ── */
  const handleReset = () => {
    setFromDate(defaultFrom);
    setToDate(today);
    setReportType("");
    setServiceType("");
    setDateError("");
    setFlowRows([]);
    setFlowSearchDetails(null);
    setFlowRowProgress(null);
    setDownloadingFlowId(null);
    setIsWhatsAppSearch(false);
    setWhatsappPhoneNumber("");
    setWhatsappWabaId("");
    setQueryParams({
      page: 1,
      limit: 25,
      startdate: defaultFrom.format("YYYY-MM-DD"),
      enddate: today.format("YYYY-MM-DD"),
      _ts: Date.now(),
    });
  };

  /* ── Download file from URL ── */
  const handleDownload = (fileUrl) => {
    if (!fileUrl) {
      toast.error("File path not available");
      return;
    }
    const link = document.createElement("a");
    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  /* ── WhatsApp row download: endpoint returns the CSV as plain text ── */
  const saveCsv = (csvText, fileName) => {
    // "\uFEFF" BOM so Excel opens UTF-8 (emoji, Hindi, etc.) correctly
    const blob = new Blob(["\uFEFF", csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // Error body arrives as text (responseHandler: "text"), so try to parse it as JSON.
  const parseWAError = (err) => {
    let body = err?.data;
    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch {
        return { code: undefined, message: body };
      }
    }
    return { code: body?.code || body?.error, message: body?.details || body?.message };
  };

  const handleWADownload = async (row) => {
    if (!customerId) {
      toast.error("Customer ID not available");
      return;
    }
    if (!row?.dlr_request_id) {
      toast.error("Request ID not found");
      return;
    }

    setDownloadingWAId(row.dlr_request_id);
    try {
      const csvText = await triggerWADownload({
        customer_id: customerId,
        request_id: row.dlr_request_id, // endpoint param is named request_id
      }).unwrap();

      if (!csvText || typeof csvText !== "string") {
        toast.error("Empty report received");
        return;
      }

      const safeName = String(row.Report_type_name || "wa_report").replace(/[^a-z0-9]+/gi, "_");
      saveCsv(csvText, `${safeName}_${row.dlr_request_id}.csv`);
      toast.success("Report downloaded successfully");
    } catch (err) {
      console.error("WA download failed", err);
      const { code, message } = parseWAError(err);
      if (err?.status === 409 && code === "REPORT_NOT_READY") {
        toast.info("Report is still being generated. Please refresh and try again.");
      } else if (err?.status === 409 && code === "REPORT_FAILED") {
        toast.error(message || "Report generation failed");
      } else if (err?.status === 404) {
        toast.error("Report file is missing. Please request it again.");
      } else {
        toast.error(message || "Failed to download WhatsApp report");
      }
    } finally {
      setDownloadingWAId(null);
    }
  };

  /* ── Table data + loading ── */
  const tableData = useMemo(() => {
    if (isWhatsAppSearch) {
      return (downloadCenterListData?.data || []).map(normalizeDownloadCenterRow);
    }
    return data?.data || [];
  }, [isWhatsAppSearch, downloadCenterListData, data]);

  const tableLoading = isWhatsAppSearch ? isDownloadCenterLoading : isLoading;
  const tableFetching = isWhatsAppSearch ? isDownloadCenterFetching : isFetching;

  const isFlowMode = Boolean(flowSearchDetails);

  const flowRowProgressPct =
    flowRowProgress && flowRowProgress.total > 0
      ? Math.round((flowRowProgress.completed / flowRowProgress.total) * 100)
      : 0;

  // Request button is enabled only when everything needed is present.
  const canRequest =
    !isRequesting &&
    !isRequestingWA &&
    serviceType !== "" &&
    Boolean(reportType) &&
    Boolean(customerId) &&
    (!isWhatsAppService || Boolean(getWhatsAppReport(selectedReport))); // pass the object

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={layoutCSS}>
        <Typography variant="h6" sx={{ mb: 1.5, fontSize: "0.9rem", fontWeight: "bold" }}>
          Download Center
        </Typography>

        {/* ── Glass filter bar (mirrors SMS report page) ── */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "0.5rem",
            mb: 2,
            backgroundColor: "#ffffff52",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
          }}
        >
          <Grid container spacing={1} alignItems="flex-start">
            <Grid item>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={handleFromDateChange}
                maxDate={today}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", sx: datePickerSx } }}
              />
            </Grid>

            <Grid item>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={handleToDateChange}
                minDate={fromDate || undefined}
                maxDate={fromDate ? fromDate.add(1, "month") : today}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: datePickerSx, error: !!dateError },
                }}
              />
              {dateError && (
                <Typography sx={{ fontSize: "0.68rem", color: "#d32f2f", mt: 0.3, maxWidth: 200 }}>
                  {dateError}
                </Typography>
              )}
            </Grid>

            <Grid item>
              <TextField
                select
                label="Service Type"
                size="small"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                sx={selectSx}
                disabled={isServiceLoading}
              >
                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>All</MenuItem>
                {serviceOptions.map((s) => (
                  <MenuItem key={s.service_id} value={s.service_id} sx={{ fontSize: "0.75rem" }}>
                    {s.service_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item>
              <TextField
                select
                label="Report Type"
                size="small"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                sx={selectSx}
                disabled={isCategoriesLoading || serviceType === ""}
              >
                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>All</MenuItem>
                {reportTypeOptions.map((cat) => (
                  <MenuItem key={cat.Report_type_id} value={cat.Report_type_id} sx={{ fontSize: "0.75rem" }}>
                    {cat.Report_type_name}
                  </MenuItem>
                ))}
              </TextField>
              {selectedReport && (
                <Typography sx={{ fontSize: "0.65rem", color: "#666", mt: 0.3, maxWidth: 210 }}>
                  {selectedReport.description || selectedReport.Report_type_name}
                </Typography>
              )}
            </Grid>

            {isWhatsAppService && (
              <>
                <Grid item>
                  <TextField
                    select
                    label="WABA ID"
                    size="small"
                    value={whatsappWabaId}
                    onChange={(e) => {
                      setWhatsappWabaId(e.target.value);
                      setWhatsappPhoneNumber(""); // phone list depends on WABA
                    }}
                    sx={selectSx}
                    disabled={isWhatsappPhoneLoading}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.75rem" }}>All</MenuItem>
                    {whatsappWabaIds.map((wabaId) => (
                      <MenuItem key={wabaId} value={wabaId} sx={{ fontSize: "0.75rem" }}>
                        {wabaId}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item>
                  <TextField
                    select
                    label="Phone Number"
                    size="small"
                    value={whatsappPhoneNumber}
                    onChange={(e) => setWhatsappPhoneNumber(e.target.value)}
                    sx={selectSx}
                    disabled={isWhatsappPhoneLoading}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.75rem" }}>All</MenuItem>
                    {filteredPhoneNumbers.map((phone) => (
                      <MenuItem
                        key={phone.phone_number_id || phone.id || phone.phone_number}
                        value={phone.phone_number}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {phone.phone_number}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            <Grid item sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                startIcon={<SearchIcon fontSize="small" />}
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  bgcolor: "#0288d1",
                  "&:hover": { bgcolor: "#0277bd" },
                  minWidth: "80px",
                }}
              >
                Search
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit}
                disabled={!canRequest}
                startIcon={
                  isRequesting || isRequestingWA ? (
                    <CircularProgress size={12} sx={{ color: "white" }} />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )
                }
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  bgcolor: "#2e7d32",
                  "&:hover": { bgcolor: "#1b5e20" },
                  minWidth: 155,
                }}
              >
                {isRequesting || isRequestingWA ? "Requesting..." : "Request Download"}
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={handleFlowSearch}
                disabled={isFlowSearching}
                startIcon={
                  isFlowSearching ? (
                    <CircularProgress size={12} sx={{ color: "white" }} />
                  ) : (
                    <AccountTreeIcon fontSize="small" />
                  )
                }
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  bgcolor: "#6a1b9a",
                  "&:hover": { bgcolor: "#4a148c" },
                  minWidth: 155,
                }}
              >
                {isFlowSearching ? "Searching..." : "Flow Report"}
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleRefresh}
                disabled={tableFetching || tableLoading}
                startIcon={
                  tableFetching || tableLoading ? (
                    <CircularProgress size={12} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )
                }
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  color: "#0288d1",
                  borderColor: "#0288d1",
                }}
              >
                Refresh
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleReset}
                disabled={isRequesting || isRequestingWA || isFlowSearching}
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  color: "#555",
                  borderColor: "#ccc",
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>

          {downloadingFlowId && flowRowProgress && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={flowRowProgressPct}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": { bgcolor: "#6a1b9a" },
                }}
              />
              <Typography sx={{ fontSize: "0.7rem", color: "#555", mt: 0.5 }}>
                Downloading flow {downloadingFlowId}: {flowRowProgress.completed} of {flowRowProgress.total} service calls
              </Typography>
            </Box>
          )}
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            {isFlowMode ? (
              <>
                <TableHead
                  sx={{
                    bgcolor: "#6a1b9a",
                    "& th": {
                      color: "white",
                      fontSize: "0.8rem",
                      padding: "6px",
                      borderBottom: "0.5px solid #1b1b1bff",
                      borderRight: "0.5px solid #1b1b1bff",
                      textAlign: "center",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Campaign Flow ID</TableCell>
                    <TableCell>Flow Name</TableCell>
                    <TableCell>Services</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isFlowSearching ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={cellSX}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : flowRows.length > 0 ? (
                    flowRows.map((flow) => {
                      const rowServices = Array.from(detectFlowServices([flow]));
                      const isRowDownloading = downloadingFlowId === flow.campaignflow_id;
                      return (
                        <TableRow key={flow.campaignflow_id}>
                          <TableCell sx={cellSX}>
                            {flow.date ? (
                              <>
                                {dayjs(flow.date).format("DD/MM/YYYY")}
                                <br />
                                {dayjs(flow.date).format("HH:mm")}
                              </>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell sx={cellSX}>{flow.campaignflow_id}</TableCell>
                          <TableCell sx={cellSX}>{flow.campaign_name || "-"}</TableCell>
                          <TableCell sx={cellSX}>
                            {rowServices.length > 0 ? (
                              <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                                {rowServices.map((s) => (
                                  <Chip
                                    key={s}
                                    label={s.toUpperCase()}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: "0.62rem",
                                      fontWeight: "bold",
                                      ...(SERVICE_CHIP_COLOR[s] || {}),
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell sx={cellSX}>
                            <Tooltip
                              title={
                                rowServices.length === 0
                                  ? "No SMS/RCS data for this flow"
                                  : "Download this flow's report"
                              }
                            >
                              <span>
                                <IconButton
                                  color="primary"
                                  disabled={rowServices.length === 0 || isRowDownloading}
                                  onClick={() => handleDownloadFlowRow(flow)}
                                  size="small"
                                >
                                  {isRowDownloading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DownloadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={cellSX}>
                        No flows found for the selected date range.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            ) : (
              <>
                <TableHead
                  sx={{
                    bgcolor: "#0288d1",
                    "& th": {
                      color: "white",
                      fontSize: "0.8rem",
                      padding: "6px",
                      borderBottom: "0.5px solid #1b1b1bff",
                      borderRight: "0.5px solid #1b1b1bff",
                      textAlign: "center",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>Apply Date</TableCell>
                    <TableCell>Report Type</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Completion Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={cellSX}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length > 0 ? (
                    tableData.map((row) => (
                      <TableRow key={row.dlr_request_id}>
                        <TableCell sx={cellSX}>
                          {dayjs(row.createdate).format("DD/MM/YYYY")}
                          <br />
                          {dayjs(row.createdate).format("HH:mm")}
                        </TableCell>
                        <TableCell sx={cellSX}>{row.Report_type_name}</TableCell>
                        <TableCell sx={cellSX}>
                          {dayjs(row.fromdate).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {dayjs(row.todate).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          <span
                            style={{
                              color:
                                row.status === "completed" ? "green"
                                  : row.status === "failed" ? "red"
                                    : row.status === "pending" ||
                                        row.status === "in_progress"
                                      ? "orange"
                                      : "#666",
                              fontWeight: "bold",
                            }}
                          >
                            {formatStatusLabel(row.status)}
                          </span>
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {row.complitiondate
                            ? dayjs(row.complitiondate).format("DD/MM/YYYY HH:mm:ss")
                            : "-"}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          <Tooltip
                            title={
                              row.status !== "completed"
                                ? `Download not available (Status: ${formatStatusLabel(row.status)})`
                                : "Download"
                            }
                          >
                            <span>
                              <IconButton
                                color="primary"
                                disabled={
                                  row.status !== "completed" ||
                                  (isWhatsAppSearch
                                    ? downloadingWAId === row.dlr_request_id
                                    : !row.filepath)
                                }
                                onClick={() =>
                                  isWhatsAppSearch
                                    ? handleWADownload(row)
                                    : handleDownload(row.filepath)
                                }
                                size="small"
                              >
                                {isWhatsAppSearch && downloadingWAId === row.dlr_request_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                color="error"
                                disabled={isWhatsAppSearch ? isDeletingWhatsapp : isDeleting}
                                onClick={() => handleDeleteRequest(row.dlr_request_id)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={cellSX}>
                        No Data Found!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            )}
          </Table>
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
};

export default DownloadCenter;